import { forwardRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Combobox, type ComboboxOption } from '../../design-system/primitives/Combobox/Combobox';
import type { FieldWidth } from '../../design-system/primitives/Field/Field';
import { ApiError } from '../../lib/api/client';
import { lookupsApi, type StaffLookup } from '../../lib/api/lookups';
import { staffOptionLabel } from './pickerLabels';

/**
 * StaffPicker — choose a person by name, hand back their uuid.
 *
 * Replaces the raw "Employee (uuid)" / "User UUID" text boxes the complaint
 * and escalation-routes screens used to have: nobody knows a colleague's
 * uuid, and the user directory that would have told them needs `user.view`.
 * `GET /lookups/staff` is the narrow endpoint built for exactly this — see
 * `lib/api/lookups.ts`.
 *
 * ## Server-filtered, so the Combobox runs in async mode
 *
 * The staff list is too long to ship whole and is confined server-side to
 * what the caller may see, so filtering here would be filtering the wrong
 * list. Opening the field loads the first page (alphabetical, no `q`) at
 * once, so it still behaves as a dropdown; typing re-queries after
 * {@link STAFF_SEARCH_DEBOUNCE_MS} of quiet, and a superseded request is
 * aborted rather than allowed to land late over a newer answer.
 *
 * Nothing is fetched until the field is first opened — a form with this
 * picker on it costs no request until someone reaches for it.
 *
 * ## The chosen label outlives the page it came from
 *
 * Once a person is picked the next search replaces `options`, and the chosen
 * person may not be on it. The picker remembers the label it committed; a
 * caller that already knows who `value` is (an existing route's
 * `user_name`) passes `selectedLabel` for the same reason.
 */

export const STAFF_SEARCH_DEBOUNCE_MS = 250;

export interface StaffPickerProps {
  label: string;
  width: FieldWidth;
  /** The chosen person's uuid, or null. */
  value: string | null;
  onChange: (uuid: string) => void;
  /** The label for `value` when it was chosen elsewhere (e.g. a saved route). */
  selectedLabel?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
}

export const StaffPicker = forwardRef<HTMLInputElement, StaffPickerProps>(function StaffPicker(
  { label, width, value, onChange, selectedLabel, error, hint, required, disabled, id, className },
  ref,
) {
  const { t } = useTranslation();

  /** `null` until the list is first opened — nothing is fetched before then. */
  const [query, setQuery] = useState<string | null>(null);
  const [results, setResults] = useState<readonly StaffLookup[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [chosen, setChosen] = useState<{ uuid: string; label: string } | null>(null);

  useEffect(() => {
    if (query === null) return undefined;

    const controller = new AbortController();
    // The first page opens at once; only typing waits for a pause.
    const delay = query.trim() === '' ? 0 : STAFF_SEARCH_DEBOUNCE_MS;
    setLoading(true);

    const timer = setTimeout(() => {
      void (async () => {
        try {
          const rows = await lookupsApi.staff({ q: query }, controller.signal);
          if (controller.signal.aborted) return;
          setResults(rows);
          setFailed(false);
        } catch (failure) {
          if (controller.signal.aborted) return;
          if (!(failure instanceof ApiError)) throw failure;
          setResults([]);
          setFailed(true);
        } finally {
          if (!controller.signal.aborted) setLoading(false);
        }
      })();
    }, delay);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  const options: readonly ComboboxOption[] = results.map((person) => ({
    value: person.uuid,
    label: staffOptionLabel(person),
  }));

  const rememberedLabel = chosen !== null && chosen.uuid === value ? chosen.label : selectedLabel;

  return (
    <Combobox
      ref={ref}
      label={label}
      width={width}
      options={options}
      value={value}
      selectedLabel={rememberedLabel}
      placeholder={t('pickers.staffPlaceholder')}
      emptyMessage={failed ? t('pickers.staffLoadFailed') : t('pickers.staffEmpty')}
      loading={loading}
      loadingMessage={t('pickers.searching')}
      onQueryChange={setQuery}
      onChange={(uuid) => {
        const option = options.find((candidate) => candidate.value === uuid);
        if (option !== undefined) setChosen({ uuid, label: option.label });
        onChange(uuid);
      }}
      error={error}
      hint={hint}
      required={required}
      disabled={disabled}
      id={id}
      className={className}
    />
  );
});
